import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'
import { queryKeys } from '@/api/queryKeys'
import type { components } from '@/../types/api'

/**
 * Recuperar canal: os lotes "arquivar vídeos fracos", com prévia, orçamento de
 * cota, desfazer e a medição de antes e depois. Ver
 * apps/backend/docs/plan-channel-recovery.md.
 */
export type RecoveryStatus = components['schemas']['RecoveryStatusOut']
export type RecoveryCriteria = components['schemas']['RecoveryCriteria']
export type RecoveryPreview = components['schemas']['RecoveryPreviewOut']
export type RecoveryCandidate = components['schemas']['RecoveryCandidateOut']
export type RecoveryBatch = components['schemas']['RecoveryBatchOut']
export type RecoveryBatchDetail = components['schemas']['RecoveryBatchDetailOut']
export type RecoveryExperiment = components['schemas']['RecoveryExperimentOut']
export type RecoveryTargetPrivacy = 'unlisted' | 'private'

/** Lote que ainda trava o canal: a lista é recarregada enquanto houver um. */
export const ACTIVE_BATCH_STATUSES: readonly RecoveryBatch['status'][] = [
  'queued',
  'running',
  'waiting',
]

export function useRecoveryStatus() {
  return useQuery({
    queryKey: queryKeys.recovery.status(),
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/channel-recovery/status')
      if (!data) throw new Error('recovery status empty')
      return data.data
    },
    staleTime: 30_000,
    retry: false,
  })
}

export function useRecoveryPreview(slug: string) {
  return useMutation({
    mutationFn: async (criteria: RecoveryCriteria) => {
      const { data } = await api.POST('/api/v1/channels/{slug}/recovery/archive-weak/preview', {
        params: { path: { slug } },
        body: criteria,
      })
      if (!data) throw new Error('recovery preview empty')
      return data.data
    },
  })
}

export function useCreateRecoveryBatch(slug: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: {
      criteria: RecoveryCriteria
      targetPrivacy: RecoveryTargetPrivacy
      videos: { video_id: string; language: string }[]
    }) => {
      const { data } = await api.POST('/api/v1/channels/{slug}/recovery/batches', {
        params: { path: { slug } },
        body: {
          criteria: input.criteria,
          target_privacy: input.targetPrivacy,
          videos: input.videos,
        },
      })
      if (!data) throw new Error('recovery batch empty')
      return data.data
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.recovery.all })
    },
  })
}

export function useRecoveryBatches(slug: string) {
  return useQuery({
    queryKey: queryKeys.recovery.batches(slug),
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/channels/{slug}/recovery/batches', {
        params: { path: { slug } },
      })
      if (!data) throw new Error('recovery batches empty')
      return data.data
    },
    enabled: Boolean(slug),
    retry: false,
    // Com lote ativo, o progresso anda sozinho na tela.
    refetchInterval: (query) =>
      (query.state.data ?? []).some((b) => ACTIVE_BATCH_STATUSES.includes(b.status))
        ? 10_000
        : false,
  })
}

export type RecoveryBatchAction = 'cancel' | 'resume' | 'undo'

export function useRecoveryBatchAction(slug: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ batchId, action }: { batchId: number; action: RecoveryBatchAction }) => {
      const params = { path: { slug, batch_id: batchId } }
      const { data } =
        action === 'cancel'
          ? await api.POST('/api/v1/channels/{slug}/recovery/batches/{batch_id}/cancel', { params })
          : action === 'resume'
            ? await api.POST('/api/v1/channels/{slug}/recovery/batches/{batch_id}/resume', {
                params,
              })
            : await api.POST('/api/v1/channels/{slug}/recovery/batches/{batch_id}/undo', { params })
      if (!data) throw new Error('recovery action empty')
      return data.data
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.recovery.all })
    },
  })
}

export function useRecoveryExperiment(slug: string) {
  return useQuery({
    queryKey: queryKeys.recovery.experiment(slug),
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/channels/{slug}/recovery/experiment', {
        params: { path: { slug } },
      })
      if (!data) throw new Error('recovery experiment empty')
      return data.data
    },
    enabled: Boolean(slug),
    retry: false,
    staleTime: 60_000,
  })
}

export function useRecoveryControl(slug: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (mark: boolean) => {
      const params = { path: { slug } }
      const { data } = mark
        ? await api.POST('/api/v1/channels/{slug}/recovery/control', { params })
        : await api.DELETE('/api/v1/channels/{slug}/recovery/control', { params })
      if (!data) throw new Error('recovery control empty')
      return data.data
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.recovery.all })
    },
  })
}
